import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { CommonService } from '@app/core/Services';
import { environment } from '@environments/environment';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import jwt_decode from 'jwt-decode';
import { CookieService } from 'ngx-cookie-service';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { PermissionType } from '../Enum/settings';
import { Guid } from 'guid-typescript';
import { RoutingPath } from '../Enum';
import { NgxSpinnerService } from 'ngx-spinner';
@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate, CanActivateChild {
  isLogin = false;
  appDomain = environment.appDomain;
  uiUrl = environment.uiUrl;
  domain = 'capium.co.uk';
  constructor(
    private router: Router,
    public oidcSecurityService: OidcSecurityService,
    @Inject(DOCUMENT) private document: Document,
    private commonService: CommonService,
    private cookieService: CookieService,
    private _Activatedroute: ActivatedRoute,
    private spinner: NgxSpinnerService
  ) {}

  canActivateChild(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | boolean {
    return this.canActivate(next, state);
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state?: RouterStateSnapshot
  ): Observable<boolean> | boolean {
    const isFreshLogin = this.document.location.href.includes('code');
    const userId = this.cookieService.get('userid') as unknown as Guid;
    if (
      userId === ('' as unknown as Guid) ||
      isFreshLogin
    ) {
      this.spinner.show();
      this.deleteAllCookies();
    }

    return this.oidcSecurityService.checkAuth().pipe(
      switchMap((loginResponse: LoginResponse) => {
        const isAuthenticated = loginResponse.isAuthenticated;

        if (!isAuthenticated) {
          this.isLogin = false;
          this.notLoggedIn();
          return of(false);
        }

        return this.handleAuthenticated(loginResponse.accessToken || '', state);
      }),
      catchError((err) => {
        this.spinner.hide();
        this.notLoggedIn();
        return of(false);
      })
    );
  }

  private handleAuthenticated(token: string, state?: RouterStateSnapshot): Observable<boolean> {
    if (token) {
      this.cookieService.set('newToken', token);

      const decoded: any = jwt_decode(token);
      this.cookieService.set('externalUserGUID', decoded.ExternalUserGUID);
      this.cookieService.set('name', decoded.name);
      this.cookieService.set('email', decoded.email);
      this.commonService.setTenantName(decoded.tenantName);

      const userData = {
        CustomerId: decoded.customerId,
      };

      sessionStorage.setItem(
        `${environment.clientId}_userData`,
        JSON.stringify(userData)
      );
    }

    this.isLogin = true;
    this.spinner.hide();

    if (state?.url === '/') {
      this.router.navigate([this.getDefaultLandingPage()]);
      return of(true);
    }

    return this.hasPermissionToAccess(state?.url || '/dashboard').pipe(
      map((hasAccess) => {
        if (hasAccess) {
          return true;
        }

        this.router.navigate([RoutingPath.AccessDenied]);
        return false;
      })
    );
  }

  private getDefaultLandingPage(): string {
    const showBookkeepingValue = localStorage.getItem('isShowBookkeeping');
    if (showBookkeepingValue !== null) {
      return showBookkeepingValue === 'true' ? RoutingPath.Dashboard : RoutingPath.OpeningBalance;
    }

    const userPermission = localStorage.getItem('userPermission');

    if (!userPermission) {
      return RoutingPath.Dashboard;
    }

    try {
      const permissions = JSON.parse(userPermission);
      if (!Array.isArray(permissions)) {
        return RoutingPath.Dashboard;
      }

      const checkMenuTree = (items: any[]): boolean =>
        items.some((item) => {
          const name = item?.name?.toLowerCase?.();
          if (name === 'bookkeeping') {
            return true;
          }

          const subMenu = Array.isArray(item?.subMenu) ? item.subMenu : [];
          return subMenu.length > 0 && checkMenuTree(subMenu);
        });

      const hasBookkeeping = checkMenuTree(permissions);
      return hasBookkeeping ? RoutingPath.Dashboard : RoutingPath.OpeningBalance;
    } catch {
      return RoutingPath.Dashboard;
    }
  }

  notLoggedIn(): void {
    const url = window.location.href;

    if (url.split('/')[url.split('/').length - 1] === 'register') {
      this.isLogin = false;
      localStorage.clear();
      sessionStorage.clear();
      this.deleteAllCookies();
      return;
    }

    localStorage.clear();
    sessionStorage.clear();
    this.deleteAllCookies();
    this.oidcSecurityService.authorize();
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      if (!decoded || !decoded.exp) {
        return true;
      }
      return decoded.exp < Math.floor(Date.now() / 1000);
    } catch {
      return true;
    }
  }

  login(): void {
    this.oidcSecurityService.authorize();
  }

  logout(): void {
    this.oidcSecurityService.logoff();
  }

  deleteAllCookies(): void {
    this.cookieService.deleteAll('/');
  }

  getAppUrl(tenantName: string): string {
    const restUrl = `${window.location.pathname}${window.location.search}`;
    return `https://${this.appDomain}.${this.domain}${restUrl}`;
  }

  setUrlIndex(url) {
    let urlIndex = '';
    if (url.indexOf(';') > 0) {
      urlIndex = ';';
    }
    if (url.indexOf('?') > 0) {
      urlIndex = '?';
    }
    return urlIndex;
  }

  hasPermissionToAccess(url): Observable<boolean> {
    let urlIndex = this.setUrlIndex(url);

    const currentUrl = url.indexOf(urlIndex) > 0 ? url.split(urlIndex)[0] : url;
    const currUrl = currentUrl.substring(1);

    const showBookkeepingValue = localStorage.getItem('isShowBookkeeping');
    if (showBookkeepingValue !== null) {
      if (currUrl === RoutingPath.Dashboard && showBookkeepingValue === 'true') {
        return of(true);
      }

      if (currUrl === 'manage/openingBalance' && showBookkeepingValue === 'false') {
        return of(true);
      }
    }

    const userPermission = localStorage.getItem('userPermission')!;
    const element = JSON.parse(userPermission);
    if (!element) {
      return of(true);
    }
    if (
      currUrl === RoutingPath.CashCoding ||
      currUrl === RoutingPath.BankManualImport ||
      currUrl === RoutingPath.ReportDetailsList ||
      currUrl === RoutingPath.CashEntryList ||
      currUrl === RoutingPath.SupportTicket ||
      currUrl === RoutingPath.AddCharity ||
      currUrl === RoutingPath.BankImport ||
      currUrl === RoutingPath.BankTransaction ||
      currUrl === RoutingPath.EditAccountProductionTrialBalance ||
      currUrl === RoutingPath.IncludeExcludeDetail ||
      currUrl === RoutingPath.submitCompaniesHouse ||
      currUrl === RoutingPath.ManualAccountProductionTrialBalance ||
      currUrl === RoutingPath.Jurnals ||
      
      currUrl === RoutingPath.submitCompaniesHouse ||
      currUrl === RoutingPath.additionalNotes ||
      currUrl === RoutingPath.additionlPolicy ||
      currUrl === RoutingPath.AutoBankReconcillation
    ) {
      return of(true);
    }

    const isMenuExist = element.some(
      (ele) => ele.url === currUrl || ele.addUrl === currUrl
    );

    if (isMenuExist || this.checkSubMenuPresent(element, currUrl)) {
      for (const ele of element) {
        if (
          (ele.url === currUrl || ele.addUrl === currUrl) &&
          ele.permissionId === PermissionType.None
        ) {
          return of(false);
        }

        for (const childElement of ele.subMenu) {
          if (
            (childElement.url === currUrl || childElement.addUrl === currUrl) &&
            childElement.permissionId === PermissionType.None
          ) {
            return of(false);
          }
        }
      }
    } else {
      return of(false);
    }

    return of(true);
  }

  checkSubMenuPresent(element, currUrl) {
    const checkSubMenu = (subMenuArray) => {
      return subMenuArray.some((submenu) => {
        if (submenu.subMenu.length) {
          return checkSubMenu(submenu.subMenu);
        }
        return submenu.url === currUrl || submenu.addUrl === currUrl;
      });
    };

    for (const ele of element) {
      if (ele.subMenu.length && checkSubMenu(ele.subMenu)) {
        return true;
      }
    }

    return false;
  }
}
